import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import reservationsSvc from '../services/reservations';
import manageareas from '../services/manageareas';
import '../style/css/stats2026.css';

/*
 * StatsPage — the REPORTS / ANALYTICS dashboard (דוחות ונתונים) for the HOPPA
 * operator app. Route /stats, nav "סטטיסטיקות". Owner-facing, show-off screen.
 *
 * Operator intent (verbatim): "דוחות ונתונים".
 *
 * All KPIs + charts are computed from REAL reservations (reservationsList range
 * queries, current + previous period for deltas). Revenue and occupancy are omitted
 * (no data source yet). Charts are PURE CSS/SVG — no chart npm deps.
 */

/* ------------------------------------------------------------------ *
 *  MOCK DATA — replace with statsDashboard {range}
 *  Keyed by range id so the picker just swaps the dataset. Each range
 *  carries the whole dashboard payload (KPIs + series) so the future act
 *  can return the same shape per period.
 * ------------------------------------------------------------------ */

// KPI shape: { value, prev }  → the UI derives the +/-% delta vs prev period.
// Series shapes are per-chart and documented inline.
// ── real-data helpers ──────────────────────────────────────────────────────
const HE_DAYS_SHORT = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

const isoD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// The current + previous window (same length, immediately before) for a range id.
function rangeWindow(rangeId) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let start; let end;
  if (rangeId === 'day') { start = today; end = today; }
  else if (rangeId === 'week') { start = addDays(today, -today.getDay()); end = addDays(start, 6); }
  else if (rangeId === 'month') { start = new Date(today.getFullYear(), today.getMonth(), 1); end = new Date(today.getFullYear(), today.getMonth() + 1, 0); }
  else if (rangeId === 'quarter') { end = today; start = addDays(today, -89); }
  else { end = today; start = addDays(today, -364); } // year
  const days = Math.round((end - start) / 86400000) + 1;
  const prevEnd = addDays(start, -1);
  const prevStart = addDays(prevEnd, -(days - 1));
  return { start, end, prevStart, prevEnd };
}

// KPI totals for a set of reservation rows.
function summarize(rows) {
  const reservations = rows.length;
  const covers = rows.reduce((s, r) => s + (Number(r.party) || 0), 0);
  const avgParty = reservations ? covers / reservations : 0;
  const noShowCount = rows.filter((r) => r.status === 'no-show').length;
  const noShow = reservations ? (noShowCount / reservations) * 100 : 0;
  return { reservations, covers, avgParty, noShow };
}

// Build the full dashboard model from real rows (current + previous) + area names.
function buildDashboard(rows, prevRows, areaName, rangeId, win) {
  const cur = summarize(rows);
  const prev = summarize(prevRows);
  const kpis = {
    reservations: { value: cur.reservations, prev: prev.reservations },
    covers: { value: cur.covers, prev: prev.covers },
    avgParty: { value: cur.avgParty, prev: prev.avgParty },
    noShow: { value: Math.round(cur.noShow * 10) / 10, prev: Math.round(prev.noShow * 10) / 10 },
  };
  // by weekday (Sun..Sat)
  const dayCounts = [0, 0, 0, 0, 0, 0, 0];
  rows.forEach((r) => { if (r.date) { const wd = new Date(`${r.date}T00:00:00`).getDay(); if (wd >= 0 && wd <= 6) dayCounts[wd] += 1; } });
  const byDay = HE_DAYS_SHORT.map((day, i) => ({ day, value: dayCounts[i] }));
  // by hour
  const hourMap = {};
  rows.forEach((r) => { const h = (r.time || '').slice(0, 2); if (h) hourMap[h] = (hourMap[h] || 0) + 1; });
  const byHour = Object.keys(hourMap).sort().map((h) => ({ h, value: hourMap[h] }));
  // by area (count + covers)
  const areaMap = {};
  rows.forEach((r) => {
    const nm = areaName[String(r.area)] || (r.area != null && Number(r.area) ? `אזור ${r.area}` : 'ללא אזור');
    if (!areaMap[nm]) areaMap[nm] = { area: nm, value: 0, covers: 0 };
    areaMap[nm].value += 1; areaMap[nm].covers += Number(r.party) || 0;
  });
  const byArea = Object.values(areaMap).sort((a, b) => b.value - a.value);
  // top customers by reservation count in this range (name from the joined guest/user)
  const custMap = {};
  rows.forEach((r) => {
    const nm = (r.name && r.name !== '—') ? r.name : null;
    if (!nm) return;
    custMap[nm] = (custMap[nm] || 0) + 1;
  });
  const topCustomers = Object.keys(custMap)
    .map((name) => ({ name, visits: custMap[name] }))
    .sort((a, b) => b.visits - a.visits)
    .slice(0, 5);
  // trend = reservation volume: per-hour for a single day, else per-day across the range
  const trend = [];
  if (rangeId === 'day') {
    for (let h = 12; h <= 23; h += 1) { const k = String(h).padStart(2, '0'); trend.push(hourMap[k] || 0); }
  } else {
    const byDate = {};
    rows.forEach((r) => { if (r.date) byDate[r.date] = (byDate[r.date] || 0) + 1; });
    // Zero-fill EVERY day in the window so quiet days count in the daily average.
    if (win) {
      for (let d = new Date(win.start); d <= win.end; d = addDays(d, 1)) { trend.push(byDate[isoD(d)] || 0); }
    } else {
      Object.keys(byDate).sort().forEach((d) => trend.push(byDate[d]));
    }
  }
  return { kpis, byDay, byHour, byArea, trend, topCustomers };
}

const RANGES = [
  { id: 'day',     label: 'היום' },
  { id: 'week',    label: 'השבוע' },
  { id: 'month',   label: 'החודש' },
  { id: 'quarter', label: '3 חודשים' },
  { id: 'year',    label: 'שנה' },
];

// Area palette — pink primary, then two neutral grays (design rule: one accent).
const AREA_COLORS = ['var(--brand)', 'var(--border-strong)', 'var(--text-faint)'];

/* ------------------------------------------------------------------ *
 *  Helpers
 * ------------------------------------------------------------------ */

// Compact Hebrew number: 3,142,000 → "3.1M", 61200 → "61.2K".
const compact = (n) => {
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e4) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString('he-IL');
};


// Delta vs previous period. For most KPIs "up" is good; for no-show up is bad,
// so we allow inverting the good/bad color mapping.
const delta = (value, prev, invert = false) => {
  if (!prev) return { pct: 0, dir: 'flat', good: true };
  const pct = ((value - prev) / prev) * 100;
  const dir = pct > 0.05 ? 'up' : pct < -0.05 ? 'down' : 'flat';
  const rising = pct > 0;
  const good = invert ? !rising : rising;
  return { pct: Math.abs(pct), dir, good };
};

/* ------------------------------------------------------------------ *
 *  Small presentational pieces
 * ------------------------------------------------------------------ */

const DeltaTag = ({ value, prev, invert }) => {
  const d = delta(value, prev, invert);
  if (d.dir === 'flat') {
    return <span className="stats-delta is-flat">— ללא שינוי</span>;
  }
  const arrow = d.dir === 'up' ? '▲' : '▼';
  return (
    <span className={`stats-delta ${d.good ? 'is-good' : 'is-bad'}`}>
      <span aria-hidden="true">{arrow}</span>
      {d.pct.toFixed(1)}%
      <span className="stats-delta-note">מהתקופה הקודמת</span>
    </span>
  );
};

/* ------------------------------------------------------------------ *
 *  Component
 * ------------------------------------------------------------------ */

const EMPTY_DASH = {
  kpis: {
    reservations: { value: 0, prev: 0 }, covers: { value: 0, prev: 0 },
    avgParty: { value: 0, prev: 0 }, noShow: { value: 0, prev: 0 },
  },
  byDay: HE_DAYS_SHORT.map((day) => ({ day, value: 0 })), byHour: [], byArea: [], trend: [], topCustomers: [],
};

const StatsPage = () => {
  const [range, setRange] = useState('week');
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Compute the dashboard from REAL reservations for the selected range + previous period.
  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      const w = rangeWindow(range);
      const [cur, prev, areaList] = await Promise.all([
        reservationsSvc.listRange({ startingDate: isoD(w.start), endingDate: isoD(w.end) }),
        reservationsSvc.listRange({ startingDate: isoD(w.prevStart), endingDate: isoD(w.prevEnd) }),
        manageareas.getAllArea(true).catch(() => []),
      ]);
      if (!alive) return;
      const areaName = {};
      (Array.isArray(areaList) ? areaList : []).forEach((a) => { areaName[String(a.id || a.ID)] = a.name; });
      setDashData(buildDashboard(cur.rows || [], prev.rows || [], areaName, range, w));
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [range]);

  const data = dashData || EMPTY_DASH;

  const kpiCards = useMemo(() => {
    const k = data.kpis;
    return [
      { key: 'reservations', label: 'סה״כ הזמנות',   value: k.reservations.value.toLocaleString('he-IL'), kpi: k.reservations },
      { key: 'covers',       label: 'סועדים (Covers)', value: k.covers.value.toLocaleString('he-IL'),      kpi: k.covers },
      { key: 'avgParty',     label: 'גודל הזמנה ממוצע', value: k.avgParty.value.toFixed(1),                 kpi: k.avgParty },
      { key: 'noShow',       label: 'אי-הגעה',         value: `${k.noShow.value}%`,                        kpi: k.noShow, invert: true },
    ];
  }, [data]);

  const maxDay = Math.max(...data.byDay.map((d) => d.value)) || 1;
  const maxHour = Math.max(...data.byHour.map((d) => d.value), 0) || 1;

  // --- reservation-volume trend polyline geometry (viewBox 100 x 40) ---
  const trend = data.trend;
  const trendGeom = useMemo(() => {
    const W = 100, H = 40, pad = 3;
    // Need at least 2 points to draw a line; fewer → empty geometry (render guards on this).
    if (trend.length < 2) return { pts: [], line: '', area: '', W, H, empty: true };
    const n = trend.length;
    const min = Math.min(...trend);
    const max = Math.max(...trend);
    const span = max - min || 1;
    const pts = trend.map((v, i) => {
      // RTL: first point on the RIGHT edge → x decreases as i grows.
      const x = W - pad - (i / (n - 1)) * (W - pad * 2);
      const y = H - pad - ((v - min) / span) * (H - pad * 2);
      return [x, y];
    });
    const line = pts.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');
    const area = `${pts[0][0].toFixed(2)},${H} ${line} ${pts[n - 1][0].toFixed(2)},${H}`;
    return { pts, line, area, W, H, empty: false };
  }, [trend]);

  // --- donut geometry for byArea ---
  const areaTotal = data.byArea.reduce((s, a) => s + a.value, 0);
  const donut = useMemo(() => {
    const R = 16, C = 2 * Math.PI * R;
    let offset = 0;
    return data.byArea.map((a, i) => {
      const frac = a.value / areaTotal;
      const seg = {
        area: a.area,
        color: AREA_COLORS[i % AREA_COLORS.length],
        dash: frac * C,
        gap: C - frac * C,
        // stroke-dashoffset positions the segment start
        offset: -offset * C,
        pct: Math.round(frac * 100),
      };
      offset += frac;
      return seg;
    });
  }, [data.byArea, areaTotal]);

  const enter = (i) => ({
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.28, delay: 0.02 * i, ease: [0.2, 0.7, 0.2, 1] },
  });

  return (
    <div className="stats-page">

      {/* ---------------- Header ---------------- */}
      <header className="stats-header">
        <div className="stats-header-main">
          <h1 className="stats-title">דוחות ונתונים</h1>
          <span className="stats-subtitle">סקירת ביצועים · {RANGES.find((r) => r.id === range).label}{loading ? ' · טוען…' : ''}</span>
        </div>
        <div className="stats-ranges" role="tablist" aria-label="בחירת טווח זמן">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={range === r.id}
              className={`stats-range ${range === r.id ? 'is-active' : ''}`}
              onClick={() => setRange(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {/* ---------------- KPI cards ---------------- */}
      <div className="stats-kpis">
        {kpiCards.map((c, i) => (
          <motion.div key={c.key} className="stats-kpi" {...enter(i)}>
            <span className="stats-kpi-label">{c.label}</span>
            <span className="stats-kpi-value">{c.value}</span>
            <DeltaTag value={c.kpi.value} prev={c.kpi.prev} invert={c.invert} />
          </motion.div>
        ))}
      </div>

      {/* ---------------- Charts grid ---------------- */}
      <div className="stats-charts">
        {/* Bar chart — reservations per weekday */}
        <motion.section className="stats-card stats-bars-card" {...enter(0)}>
          <div className="stats-card-head">
            <h2 className="stats-card-title">הזמנות לפי יום</h2>
            <span className="stats-card-sub">שיא ביום {data.byDay.reduce((a, b) => (b.value > a.value ? b : a)).day}</span>
          </div>
          <div className="stats-bars" role="img" aria-label="הזמנות לפי יום בשבוע">
            {data.byDay.map((d) => (
              <div className="stats-bar-col" key={d.day}>
                <span className="stats-bar-val">{compact(d.value)}</span>
                <div className="stats-bar-track">
                  <div
                    className="stats-bar-fill"
                    style={{ height: `${(d.value / maxDay) * 100}%` }}
                  />
                </div>
                <span className="stats-bar-lbl">{d.day}</span>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Reservation-volume trend — SVG area + polyline */}
        <motion.section className="stats-card stats-trend-card" {...enter(1)}>
          <div className="stats-card-head">
            <h2 className="stats-card-title">מגמת הזמנות</h2>
            <span className="stats-card-sub">
              {trend.length ? `ממוצע ${Math.round(trend.reduce((a, b) => a + b, 0) / trend.length)} ליום` : ''}
            </span>
          </div>
          <div className="stats-trend">
            {trendGeom.empty ? (
              <div className="stats-empty">אין מספיק נתונים למגמה</div>
            ) : (
            <svg
              className="stats-trend-svg"
              viewBox={`0 0 ${trendGeom.W} ${trendGeom.H}`}
              preserveAspectRatio="none"
              role="img"
              aria-label="מגמת הזמנות לאורך התקופה"
            >
              <defs>
                <linearGradient id="statsTrendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--brand)" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* gridlines */}
              {[0.25, 0.5, 0.75].map((g) => (
                <line
                  key={g}
                  x1="0" x2={trendGeom.W}
                  y1={trendGeom.H * g} y2={trendGeom.H * g}
                  className="stats-trend-grid"
                />
              ))}
              <polygon points={trendGeom.area} fill="url(#statsTrendFill)" />
              <polyline points={trendGeom.line} className="stats-trend-line" />
              {trendGeom.pts.map((p, i) => (
                <circle key={i} cx={p[0]} cy={p[1]} r="0.9" className="stats-trend-dot" />
              ))}
            </svg>
            )}
            <div className="stats-trend-axis" aria-hidden="true">
              <span>תחילת התקופה</span>
              <span>סוף התקופה</span>
            </div>
          </div>
        </motion.section>

        {/* Area split — donut + legend */}
        <motion.section className="stats-card stats-donut-card" {...enter(2)}>
          <div className="stats-card-head">
            <h2 className="stats-card-title">פילוח לפי אזור</h2>
            <span className="stats-card-sub">{data.byArea.length} אזורים</span>
          </div>
          <div className="stats-donut-wrap">
            <svg className="stats-donut-svg" viewBox="0 0 40 40" role="img" aria-label="פילוח הזמנות לפי אזור">
              <circle cx="20" cy="20" r="16" className="stats-donut-track" />
              {donut.map((s, i) => (
                <circle
                  key={i}
                  cx="20" cy="20" r="16"
                  className="stats-donut-seg"
                  stroke={s.color}
                  strokeDasharray={`${s.dash} ${s.gap}`}
                  strokeDashoffset={s.offset}
                />
              ))}
              <text x="20" y="19" className="stats-donut-total">{compact(areaTotal)}</text>
              <text x="20" y="24" className="stats-donut-cap">הזמנות</text>
            </svg>
            <ul className="stats-donut-legend">
              {donut.map((s, i) => (
                <li key={s.area}>
                  <span className="stats-legend-dot" style={{ background: AREA_COLORS[i % AREA_COLORS.length] }} />
                  <span className="stats-legend-name">{s.area}</span>
                  <span className="stats-legend-pct">{s.pct}%</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Peak hours — horizontal bars */}
        <motion.section className="stats-card stats-hours-card" {...enter(3)}>
          <div className="stats-card-head">
            <h2 className="stats-card-title">שעות שיא</h2>
            <span className="stats-card-sub">
              {data.byHour.length
                ? <>שיא בשעה <strong dir="ltr">{data.byHour.reduce((a, b) => (b.value > a.value ? b : a), { h: '—', value: 0 }).h}:00</strong></>
                : 'אין נתונים'}
            </span>
          </div>
          <div className="stats-hours">
            {data.byHour.map((d) => {
              const isPeak = d.value === maxHour;
              return (
                <div className={`stats-hour-row ${isPeak ? 'is-peak' : ''}`} key={d.h}>
                  <span className="stats-hour-lbl" dir="ltr">{d.h}:00</span>
                  <div className="stats-hour-track">
                    <div className="stats-hour-fill" style={{ width: `${(d.value / maxHour) * 100}%` }} />
                  </div>
                  <span className="stats-hour-val">{compact(d.value)}</span>
                </div>
              );
            })}
          </div>
        </motion.section>
      </div>

      {/* ---------------- Performance table + top customers ---------------- */}
      <div className="stats-lower">
        <motion.section className="stats-card stats-table-card" {...enter(0)}>
          <div className="stats-card-head">
            <h2 className="stats-card-title">טבלת ביצועים לפי אזור</h2>
          </div>

          {/* Desktop table */}
          <table className="stats-table">
            <thead>
              <tr>
                <th>אזור</th>
                <th>הזמנות</th>
                <th>סועדים</th>
                <th>נתח</th>
              </tr>
            </thead>
            <tbody>
              {data.byArea.map((a, i) => (
                <tr key={a.area}>
                  <td className="stats-td-area">
                    <span className="stats-legend-dot" style={{ background: AREA_COLORS[i % AREA_COLORS.length] }} />
                    {a.area}
                  </td>
                  <td>{a.value.toLocaleString('he-IL')}</td>
                  <td>{a.covers.toLocaleString('he-IL')}</td>
                  <td>{areaTotal ? Math.round((a.value / areaTotal) * 100) : 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile cards */}
          <ul className="stats-table-cards">
            {data.byArea.map((a, i) => (
              <li key={a.area} className="stats-table-card-row">
                <div className="stats-tc-top">
                  <span className="stats-tc-area">
                    <span className="stats-legend-dot" style={{ background: AREA_COLORS[i % AREA_COLORS.length] }} />
                    {a.area}
                  </span>
                  <span className="stats-tc-share">{areaTotal ? Math.round((a.value / areaTotal) * 100) : 0}%</span>
                </div>
                <div className="stats-tc-meta">
                  <span><b>{a.value.toLocaleString('he-IL')}</b> הזמנות</span>
                  <span><b>{a.covers.toLocaleString('he-IL')}</b> סועדים</span>
                </div>
              </li>
            ))}
          </ul>
        </motion.section>

        <motion.section className="stats-card stats-top-card" {...enter(1)}>
          <div className="stats-card-head">
            <h2 className="stats-card-title">לקוחות מובילים</h2>
          </div>
          <ol className="stats-top-list">
            {data.topCustomers.length === 0 && <li className="stats-empty">אין נתונים לתקופה זו</li>}
            {data.topCustomers.map((c, i) => (
              <li className="stats-top-row" key={c.name}>
                <span className="stats-top-rank">{i + 1}</span>
                <span className="stats-top-name">{c.name}</span>
                <span className="stats-top-visits">{c.visits} ביקורים</span>
              </li>
            ))}
          </ol>
        </motion.section>
      </div>
    </div>
  );
};

export default StatsPage;
